import { useCallback, useEffect, useRef, useState } from "react";
import SHA256 from "crypto-js/sha256";
import { api } from "./apiClient";

const EVENT = "planaett-exportacion-eoer";
const key = () => `eoer-exportacion:${localStorage.getItem("username") || sessionStorage.getItem("username") || "user"}`;
const readPending = () => {
	try { return JSON.parse(localStorage.getItem(key()) || "null"); } catch { return null; }
};
const writePending = (pending) => {
	if (pending) localStorage.setItem(key(), JSON.stringify(pending));
	else localStorage.removeItem(key());
	window.dispatchEvent(new Event(EVENT));
};

export const getExportacionesEoer = async () => {
	const response = await api.get("preparacionExportacionFichajes.php", { params: { exportStatus: 1 } });
	if (!response.data?.success) throw new Error("No se pudo consultar el estado de la exportacion.");
	return response.data;
};

export const getDetalleExportacionEoer = async (id) => {
	const response = await api.get("preparacionExportacionFichajes.php", { params: { exportDetail: id } });
	return response.data;
};

export const ejecutarExportacionEoer = async ({ action = "prepare", items = [] } = {}) => {
	const fingerprint = SHA256(JSON.stringify([action, items])).toString();
	let pending = readPending();
	if (pending && pending.fingerprint !== fingerprint) {
		const status = await getExportacionesEoer();
		const previous = status.historial.find((row) => row.request_id === pending.requestId);
		if (status.running || !previous || previous.estado === "ejecutando") {
			throw new Error("Hay una exportacion pendiente de confirmar. Espera a comprobar su estado antes de cambiar el envio.");
		}
		pending = null;
	}
	if (!pending) {
		const requestId = window.crypto.randomUUID?.() || Array.from(
			window.crypto.getRandomValues(new Uint8Array(16)), (value) => value.toString(16).padStart(2, "0")
		).join("");
		pending = { requestId, fingerprint };
	}
	writePending(pending);
	try {
		const response = await api.post("preparacionExportacionFichajes.php", {
			action, items, requestId: pending.requestId,
		}, { timeout: 300000 });
		if (!response.data?.pending && !response.data?.unconfirmed) writePending(null);
		return response.data;
	} catch (error) {
		if ([400, 401, 403, 405].includes(error.response?.status)) {
			writePending(null);
			throw error;
		}
		const uncertain = new Error("No se recibio la respuesta. La exportacion puede seguir en marcha; consulta el historial. Los fichajes no se reenviaran automaticamente.");
		uncertain.code = "EXPORT_STATUS_UNKNOWN";
		throw uncertain;
	}
};

export function useExportacionesEoer(onCompleted) {
	const [status, setStatus] = useState({ running: true, historial: [], error: "" });
	const callbackRef = useRef(onCompleted);
	const refreshRef = useRef(() => {});
	useEffect(() => { callbackRef.current = onCompleted; }, [onCompleted]);
	useEffect(() => {
		let stopped = false;
		let timer;
		let generation = 0;
		let lastTerminal = null;
		const refresh = async () => {
			clearTimeout(timer);
			const current = ++generation;
			try {
				const next = await getExportacionesEoer();
				if (stopped || current !== generation) return;
				const pending = readPending();
				const recovered = next.historial.find((row) => row.request_id === pending?.requestId && row.estado !== "ejecutando");
				const terminal = next.historial.filter((row) => row.estado !== "ejecutando").map((row) => `${row.id}:${row.estado}`).join("|");
				setStatus({ ...next, error: "" });
				if (recovered) localStorage.removeItem(key());
				if (recovered || (lastTerminal !== null && lastTerminal !== terminal)) callbackRef.current?.();
				lastTerminal = terminal;
			} catch {
				if (stopped || current !== generation) return;
				setStatus((previous) => ({ ...previous, running: true, error: "No se puede comprobar el estado. La exportacion queda bloqueada hasta recuperar la conexion." }));
			}
			if (!stopped && current === generation) timer = setTimeout(refresh, 5000);
		};
		refreshRef.current = refresh;
		refresh();
		window.addEventListener(EVENT, refresh);
		window.addEventListener("storage", refresh);
		return () => {
			stopped = true;
			clearTimeout(timer);
			window.removeEventListener(EVENT, refresh);
			window.removeEventListener("storage", refresh);
		};
	}, []);
	const refresh = useCallback(() => refreshRef.current(), []);
	return { ...status, refresh };
}
